import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../../../context/CartContext';
import { useSearch } from '../../../context/SearchContext';
import './BottomTabBar.css';

/* ─────────────────────────────────────────────
   Tab config — edit destinations here only
───────────────────────────────────────────── */
// NOTE: Cart and Search are handled via context (drawer/modal),
// not navigation links, so they don't have a `to` prop.
const TABS = [
  {
    id: 'home',
    to: '/',
    label: 'Home',
    exact: true,
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
  },
  {
    id: 'shop',
    to: '/shop',
    label: 'Shop',
    exact: false,
    matchPaths: ['/men', '/women', '/shop', '/shop/men', '/shop/women', '/product'],
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
        <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
      </svg>
    ),
  },
  {
    id: 'search',
    label: 'Search',
    action: 'search', // handled via onClick, not navigation
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="11" cy="11" r="8"/>
        <path d="m21 21-4.35-4.35"/>
      </svg>
    ),
  },
  {
    id: 'cart',
    label: 'Cart',
    action: 'cart',  // opens cart drawer
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
        <line x1="3" y1="6" x2="21" y2="6"/>
        <path d="M16 10a4 4 0 0 1-8 0"/>
      </svg>
    ),
  },
  {
    id: 'account',
    to: '/account',
    label: 'Account',
    exact: false,
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    ),
  },
];

function BottomTabBar() {
  const location   = useLocation();
  const { cartItems, openCart } = useCart();
  const { openSearch } = useSearch();

  const totalUnits = cartItems.reduce((s, i) => s + i.quantity, 0);

  const isActive = (tab) => {
    if (tab.action) return false; // action tabs are never "active" in nav sense
    if (tab.exact)  return location.pathname === tab.to;
    if (tab.matchPaths) return tab.matchPaths.some(p => location.pathname.startsWith(p));
    return location.pathname.startsWith(tab.to);
  };

  const handleAction = (action) => {
    if (action === 'cart')   openCart();
    if (action === 'search') openSearch();
  };

  return (
    <nav className="btb" aria-label="Main navigation">
      {TABS.map(tab => {
        const active = isActive(tab);
        const isCart = tab.id === 'cart';

        const content = (
          <>
            <span className="btb__icon-wrap">
              {tab.icon}
              {isCart && totalUnits > 0 && (
                <span className="btb__badge" aria-label={`${totalUnits} item${totalUnits !== 1 ? 's' : ''} in cart`}>
                  {totalUnits > 99 ? '99+' : totalUnits}
                </span>
              )}
            </span>
            <span className="btb__label">{tab.label}</span>
            {active && <span className="btb__indicator" aria-hidden="true" />}
          </>
        );

        if (tab.action) {
          return (
            <button
              key={tab.id}
              type="button"
              className={`btb__tab${active ? ' btb__tab--active' : ''}`}
              onClick={() => handleAction(tab.action)}
              aria-label={tab.label}
            >
              {content}
            </button>
          );
        }

        return (
          <Link
            key={tab.id}
            to={tab.to}
            className={`btb__tab${active ? ' btb__tab--active' : ''}`}
            aria-label={tab.label}
            aria-current={active ? 'page' : undefined}
          >
            {content}
          </Link>
        );
      })}
    </nav>
  );
}

export default BottomTabBar;
